/**
 * ============================================
 * SCHEDULE PAGE - Motion-Style Modern Calendar
 * ============================================
 * 
 * CURRENT STRUCTURE:
 * - Uses FullCalendar library (@fullcalendar/react) for rendering
 * - Data source: DataContext provides jobs, teams
 * - Work items are extracted from jobs and displayed as calendar events
 * - Firestore paths: /companies/{companyId}/jobs/{jobId}
 * - Work items stored within job documents as workItems array
 * 
 * FUNCTIONALITY (PRESERVED):
 * - Week/Month view toggle
 * - Navigation (Prev/Next/Today)
 * - Drag-and-drop rescheduling
 * - Event click to view details
 * - Print day schedule (PDF)
 * - Schedule estimate dialog
 * - Quick view modal for work items
 * 
 * MOTION-STYLE VISUAL HIERARCHY:
 * 1. Full-height calendar: Fills viewport minus header (calc(100vh - 84px))
 * 2. Compact header: Title + controls on same row, minimal padding
 * 3. Full-width card: Max 1600px, tight 16-24px padding inside
 * 4. Motion-style grid: Thin grid lines (rgba 0.07), no heavy backgrounds
 * 5. Today highlight: Subtle background tint (rgba 0.08) + border accents
 * 6. Job-type legend: Compact pills above grid
 * 7. Event blocks: Light background + colored border, two-line format
 * 
 * EVENT RENDERING (Motion-style):
 * - Uses custom eventContent to render two-line format:
 *   Line 1 (bold): "JobType – CustomerName"
 *   Line 2 (normal): "Job #123"
 * - Light background color with 1px colored border
 * - Rounded rectangle (6px radius) with subtle shadow
 * - Hover: Subtle lift effect
 * 
 * HEIGHT CALCULATION:
 * - Page: height: 100vh, padding minimal (16-24px)
 * - Header: ~60px (compact title + controls)
 * - Card: flex: 1, minHeight: calc(100vh - 84px)
 * - Calendar: height: 100% to fill card
 * - Result: Calendar fills remaining viewport space, shows full day without cutting off
 */

import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useData } from '../contexts/DataContext';
import moment from 'moment';
import {
  Today as TodayIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import { ensureHttps, logUrl } from '../utils/urlUtils';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

// Job type colors - Motion-style softer, muted palette (not neon/saturated)
const JOB_TYPE_COLORS = {
  estimate: '#60A5FA',      // Soft blue (lighter, less saturated)
  install: '#34D399',        // Soft green (muted)
  repair: '#FBBF24',         // Warm yellow (softer than amber)
  maintenance: '#A78BFA',    // Violet but muted (less purple)
  inspection: '#22D3EE',     // Soft teal (lighter)
  demolition: '#F87171',     // Soft red (less intense)
  default: '#6B7280'         // Gray (fallback)
};

// Light background colors for events (Motion-style: light background + colored border)
const JOB_TYPE_BG_COLORS = {
  estimate: 'rgba(96, 165, 250, 0.12)',    // Light blue background
  install: 'rgba(52, 211, 153, 0.12)',     // Light green background
  repair: 'rgba(251, 191, 36, 0.12)',      // Light yellow background
  maintenance: 'rgba(167, 139, 250, 0.12)', // Light violet background
  inspection: 'rgba(34, 211, 238, 0.12)',  // Light teal background
  demolition: 'rgba(248, 113, 113, 0.12)', // Light red background
  default: 'rgba(107, 114, 128, 0.12)'     // Light gray background
};

// Job type display labels
const JOB_TYPE_LABELS = {
  estimate: 'Estimate',
  install: 'Install',
  repair: 'Repair',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  demolition: 'Demolition',
};

// Infer job type from work item name/description
const getJobType = (workItem) => {
  const text = `${workItem.name || ''} ${workItem.description || ''}`.toLowerCase();
  
  if (text.includes('estimate') || text.includes('quote')) return 'estimate';
  if (text.includes('install') || text.includes('installation')) return 'install';
  if (text.includes('repair') || text.includes('fix')) return 'repair';
  if (text.includes('maintenance') || text.includes('service')) return 'maintenance';
  if (text.includes('inspect') || text.includes('inspection')) return 'inspection';
  if (text.includes('demolition') || text.includes('demo')) return 'demolition';
  
  return 'default';
};

export default function Schedule() {
  const { jobs, teams, updateJob } = useData();
  const [selectedDate, setSelectedDate] = useState(moment());
  const [calendarView, setCalendarView] = useState('timeGridWeek');
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewWork, setQuickViewWork] = useState(null);
  const [scheduleEstimateOpen, setScheduleEstimateOpen] = useState(false);
  const [estimateSchedule, setEstimateSchedule] = useState({
    date: moment(),
    team: '',
    notes: ''
  });
  const [reschedulingError, setReschedulingError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Conflict detection state
  const [conflict, setConflict] = useState(null);
  const calendarRef = React.useRef(null);

  // Check if we should open the schedule estimate dialog
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('type') === 'estimate' || params.get('customerId')) {
      setScheduleEstimateOpen(true);
    }
  }, [location]);

  // Check for team conflicts
  React.useEffect(() => {
    if (!estimateSchedule.team || !estimateSchedule.date) {
      setConflict(null);
      return;
    }
    const teamWork = jobs.flatMap(job =>
      (job.workItems || []).filter(work => work.team === estimateSchedule.team && work.scheduledDate)
    );
    const selectedTime = moment(estimateSchedule.date);
    const conflictItem = teamWork.find(work => {
      const workTime = moment(work.scheduledDate);
      return Math.abs(selectedTime.diff(workTime, 'minutes')) < 60;
    });
    if (conflictItem) {
      setConflict(conflictItem);
    } else {
      setConflict(null);
    }
  }, [estimateSchedule.team, estimateSchedule.date, jobs]);

  // Flatten all work items from all jobs
  const allWorkItems = jobs.flatMap(job =>
    (job.workItems || []).map(work => ({
      ...work,
      parentJobId: job.id,
      parentJobName: job.name || job.jobId,
      customerName: job.customerName,
      address: job.address,
      parentJob: job
    }))
  );

  // Convert work items to FullCalendar events with Motion-style styling
  // Events use light background + colored border (not solid colored blocks)
  const calendarEvents = useMemo(() => {
    return allWorkItems
      .filter(work => work.scheduledDate)
      .map(work => {
        const jobType = getJobType(work);
        const start = moment(work.scheduledDate);
        const end = work.endDate 
          ? moment(work.endDate) 
          : moment(start).add(2, 'hours');

        // Format event title: JobType – CustomerName
        const typeLabel = JOB_TYPE_LABELS[jobType] || 'Work';
        const customerName = work.customerName || 'Customer';
        const jobId = work.parentJob?.jobId || work.parentJobId;
        const eventTitle = `${typeLabel} – ${customerName}`;

        return {
          id: work.id || `${work.parentJobId}-${work.name}-${work.scheduledDate}`,
          title: eventTitle,
          start: start.toISOString(),
          end: end.toISOString(),
          backgroundColor: JOB_TYPE_BG_COLORS[jobType] || JOB_TYPE_BG_COLORS.default, // Light background
          borderColor: JOB_TYPE_COLORS[jobType], // Colored border
          textColor: '#E5E7EB', // Light text color for dark theme
          borderWidth: '1px',
          classNames: ['motion-style-event'],
          extendedProps: {
            workItem: work,
            jobType: jobType,
            team: work.team,
            status: work.status,
            parentJobId: work.parentJobId,
            customerName: work.customerName,
            address: work.address,
            jobId: jobId,
            typeLabel: typeLabel,
          }
        };
      });
  }, [allWorkItems]);

  const startOfWeek = moment(selectedDate).startOf('week');
  const endOfWeek = moment(selectedDate).endOf('week');
  const weekRange = `${startOfWeek.format('MMMM D')} - ${endOfWeek.format('D, YYYY')}`;

  const getWorkItemsForDay = (date) => {
    return allWorkItems.filter(work => 
      work.scheduledDate && 
      moment(work.scheduledDate).isSame(date, 'day')
    ).sort((a, b) => moment(a.scheduledDate).diff(moment(b.scheduledDate)));
  };

  const handlePreviousWeek = () => {
    const newDate = moment(selectedDate).subtract(1, 'week');
    setSelectedDate(newDate);
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(newDate.toDate());
    }
  };

  const handleNextWeek = () => {
    const newDate = moment(selectedDate).add(1, 'week');
    setSelectedDate(newDate);
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(newDate.toDate());
    }
  };

  const handleToday = () => {
    const today = moment();
    setSelectedDate(today);
    if (calendarRef.current) {
      calendarRef.current.getApi().today();
      calendarRef.current.getApi().gotoDate(today.toDate());
    }
  };

  const handleViewChange = (view) => {
    setCalendarView(view.view.type);
    if (view.view.currentStart) {
      setSelectedDate(moment(view.view.currentStart));
    }
  };

  const handleWorkClick = (work) => {
    setQuickViewWork(work);
    setQuickViewOpen(true);
  };

  const handleQuickViewClose = () => {
    setQuickViewOpen(false);
    setQuickViewWork(null);
  };

  // Handle event drop (drag and drop rescheduling)
  const handleEventDrop = async (dropInfo) => {
    try {
      setReschedulingError(null);
      const event = dropInfo.event;
      const workItem = event.extendedProps.workItem;
      const newStart = moment(dropInfo.event.start);
      const newEnd = dropInfo.event.end ? moment(dropInfo.event.end) : moment(newStart).add(2, 'hours');

      const parentJob = jobs.find(job => job.id === workItem.parentJobId);
      if (!parentJob) {
        throw new Error('Parent job not found');
      }

      const workItemIndex = (parentJob.workItems || []).findIndex(item => {
        if (item.id && workItem.id && item.id === workItem.id) return true;
        if (item.name === workItem.name && 
            item.scheduledDate === workItem.scheduledDate) return true;
        if (!item.id && !workItem.id &&
            item.name === workItem.name &&
            item.description === workItem.description &&
            item.scheduledDate === workItem.scheduledDate) return true;
        return false;
      });

      if (workItemIndex === -1) {
        throw new Error('Work item not found in job');
      }

      const updatedWorkItems = [...(parentJob.workItems || [])];
      updatedWorkItems[workItemIndex] = {
        ...updatedWorkItems[workItemIndex],
        scheduledDate: newStart.toISOString(),
        endDate: newEnd.toISOString(),
        updatedAt: new Date().toISOString()
      };

      await updateJob(parentJob.id, {
        workItems: updatedWorkItems,
        updatedAt: new Date().toISOString()
      });

      console.log('Work item rescheduled successfully');
    } catch (error) {
      console.error('Error rescheduling work item:', error);
      setReschedulingError(error.message || 'Failed to reschedule work item. Please try again.');
      dropInfo.revert();
    }
  };

  const handleEventClick = (clickInfo) => {
    const workItem = clickInfo.event.extendedProps.workItem;
    setQuickViewWork(workItem);
    setQuickViewOpen(true);
  };

  const handleDateClick = (dateClickInfo) => {
    const clickedDate = moment(dateClickInfo.date);
    setSelectedDate(clickedDate);
  };

  const handlePrintDay = async (date) => {
    const dayWorkItems = getWorkItemsForDay(date);
    if (dayWorkItems.length === 0) {
      alert('No work scheduled for this day');
      return;
    }

    const jobsForDay = {};
    dayWorkItems.forEach(work => {
      const jobId = work.parentJobId;
      if (!jobsForDay[jobId]) {
        const job = jobs.find(j => j.id === jobId);
        if (job) {
          jobsForDay[jobId] = {
            job: job,
            workItems: []
          };
        }
      }
      if (jobsForDay[jobId]) {
        jobsForDay[jobId].workItems.push(work);
      }
    });

    try {
      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 24;
      const contentWidth = pageWidth - 2 * margin;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidth, pageHeight]
      });

      let yPosition = margin;
      const lineHeight = 14;
      const sectionSpacing = 20;

      pdf.setFontSize(18);
      pdf.setTextColor(25, 118, 210);
      pdf.text('Daily Work Schedule', margin, yPosition);
      yPosition += lineHeight * 1.5;

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Date: ${moment(date).format('MMMM D, YYYY')}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.text(`Total Jobs: ${Object.keys(jobsForDay).length}`, margin, yPosition);
      yPosition += sectionSpacing;

      Object.values(jobsForDay).forEach(({ job, workItems }, index) => {
        if (yPosition > pageHeight - margin - 100) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(25, 118, 210);
        pdf.text(`Job #${job.jobId || job.id}`, margin, yPosition);
        yPosition += lineHeight * 1.2;

        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Customer: ${job.customerName || 'N/A'}`, margin, yPosition);
        yPosition += lineHeight;
        
        if (job.address) {
          pdf.text(`Address: ${job.address}`, margin, yPosition);
          yPosition += lineHeight;
        }

        workItems.forEach(work => {
          if (yPosition > pageHeight - margin - 60) {
            pdf.addPage();
            yPosition = margin;
          }

          yPosition += lineHeight * 0.5;
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          pdf.text(`  • ${work.name || work.description || 'Work Item'}`, margin + 10, yPosition);
          yPosition += lineHeight;

          if (work.scheduledDate) {
            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`    Time: ${moment(work.scheduledDate).format('h:mm A')}`, margin + 20, yPosition);
            yPosition += lineHeight;
          }

          if (work.team && teams) {
            const team = teams.find(t => t.id === work.team);
            if (team) {
              pdf.text(`    Team: ${team.name}`, margin + 20, yPosition);
              yPosition += lineHeight;
            }
          }
        });

        yPosition += sectionSpacing;
      });

      const fileName = `daily-schedule-${moment(date).format('YYYY-MM-DD')}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleScheduleEstimate = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const customerId = params.get('customerId');
      
      const newWorkItem = {
        type: 'estimate',
        scheduledDate: estimateSchedule.date.toISOString(),
        team: estimateSchedule.team,
        notes: estimateSchedule.notes,
        status: 'Not Started',
        createdAt: new Date().toISOString(),
        customerId: customerId || null
      };

      setScheduleEstimateOpen(false);
      setEstimateSchedule({
        date: moment(),
        team: '',
        notes: ''
      });

      if (customerId) {
        navigate(`/customers/${customerId}`);
      } else {
        navigate('/schedule');
      }
    } catch (error) {
      console.error('Error scheduling estimate:', error);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: '#111827', // Very dark gray page background
        // Motion-style: Minimal padding, maximize calendar space
        padding: 0,
        paddingLeft: { md: '280px' }, // Clear sidebar space
        paddingRight: { xs: 1, sm: 1.5 },
        paddingTop: { xs: 1, sm: 1.5 }, // Minimal top padding - bring content to top
        maxWidth: 'none',
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start', // Left-align all content
      }}
    >

      {/* Top Header Section - Motion-style compact layout */}
      {/* Controls are on same row as title, right-aligned */}
      <Box
        sx={{
          mb: 0.5, // Motion-style: Minimal gap between header and calendar
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row', // Always row layout for compactness
          justifyContent: 'space-between',
          alignItems: 'center', // Vertically center title and controls on same line
          gap: 2,
          width: '100%',
          paddingX: { xs: 1, sm: 1.5 }, // Horizontal padding for header
          paddingY: { xs: 0.75, sm: 1 }, // Minimal vertical padding
          alignSelf: 'flex-start',
        }}
      >
        {/* Left: Title & Subtitle */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }, // Smaller, more compact
              mb: 0,
              lineHeight: 1.2,
            }}
          >
            Schedule
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#9CA3AF', // Light gray
              fontSize: '0.75rem',
              display: 'block',
              mt: 0.25,
            }}
          >
            {calendarView === 'timeGridWeek' 
              ? `Week of ${weekRange}` 
              : `${moment(selectedDate).format('MMMM YYYY')}`
            }
          </Typography>
        </Box>

        {/* Right: Navigation Controls - Compact Motion-style buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            flexWrap: 'nowrap',
            alignItems: 'center',
          }}
        >
          {/* Prev/Next/Today */}
          <Button
            size="small"
            onClick={handlePreviousWeek}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              backgroundColor: '#1F2937', // Dark gray
              color: '#E5E7EB', // Light gray text
              borderRadius: '9999px', // Full pill
              border: 'none',
              '&:hover': {
                backgroundColor: '#374151', // Slightly lighter on hover
              },
            }}
            startIcon={<NavigateBeforeIcon />}
          >
            Prev
          </Button>

          <Button
            size="small"
            onClick={handleToday}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              backgroundColor: '#1F2937',
              color: '#E5E7EB',
              borderRadius: '9999px',
              border: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              },
            }}
            startIcon={<TodayIcon />}
          >
            Today
          </Button>

          <Button
            size="small"
            onClick={handleNextWeek}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              backgroundColor: '#1F2937',
              color: '#E5E7EB',
              borderRadius: '9999px',
              border: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              },
            }}
            endIcon={<NavigateNextIcon />}
          >
            Next
          </Button>

          {/* View Toggle */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              backgroundColor: '#1F2937',
              borderRadius: '9999px',
              p: 0.5,
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setCalendarView('timeGridWeek');
                if (calendarRef.current) {
                  calendarRef.current.getApi().changeView('timeGridWeek');
                }
              }}
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 0.75,
                backgroundColor: calendarView === 'timeGridWeek' ? '#3B82F6' : 'transparent',
                color: calendarView === 'timeGridWeek' ? 'white' : '#E5E7EB',
                borderRadius: '9999px',
                '&:hover': {
                  backgroundColor: calendarView === 'timeGridWeek' ? '#3B82F6' : '#374151',
                },
              }}
            >
              Week
            </Button>
            <Button
              size="small"
              onClick={() => {
                setCalendarView('dayGridMonth');
                if (calendarRef.current) {
                  calendarRef.current.getApi().changeView('dayGridMonth');
                }
              }}
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 0.75,
                backgroundColor: calendarView === 'dayGridMonth' ? '#3B82F6' : 'transparent',
                color: calendarView === 'dayGridMonth' ? 'white' : '#E5E7EB',
                borderRadius: '9999px',
                '&:hover': {
                  backgroundColor: calendarView === 'dayGridMonth' ? '#3B82F6' : '#374151',
                },
              }}
            >
              Month
            </Button>
          </Box>

          {/* Print Button */}
          <Button
            size="small"
            onClick={() => handlePrintDay(selectedDate)}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              backgroundColor: '#1F2937',
              color: '#E5E7EB',
              borderRadius: '9999px',
              border: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              },
            }}
            startIcon={<PrintIcon />}
          >
            Print Day ({selectedDate.format('M/D')})
          </Button>
        </Box>
      </Box>

      {/* Schedule Card - Motion-style full-width, tight padding */}
      {/* 
        HEIGHT CALCULATION: 
        - Uses flex: 1 to fill remaining viewport height
        - Header is ~60px (compact title + controls)
        - Top padding is ~24px total
        - Card fills: calc(100vh - 84px) for maximum calendar space
        - This ensures calendar never cuts off and shows full day
      */}
      <Paper
        sx={{
          backgroundColor: '#1F2937', // Slightly lighter dark gray
          borderRadius: '8px', // Smaller radius for modern look
          // Motion-style: Tight padding inside card (16-24px)
          padding: { xs: 1, sm: 1.5 }, // 16-24px padding
          paddingTop: { xs: 1, sm: 1.25 }, // Even tighter top
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          width: '100%',
          // Full-height: Fill viewport minus header and minimal padding
          flex: 1,
          minHeight: 'calc(100vh - 84px)', // Header ~60px + padding ~24px = 84px
          maxWidth: '1600px', // Motion-style: Comfortable max width (1400-1600px range)
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: 0,
          marginLeft: 0,
          alignSelf: 'flex-start',
        }}
      >
        {reschedulingError && (
          <Alert 
            severity="error" 
            onClose={() => setReschedulingError(null)} 
            sx={{ mb: 2, backgroundColor: '#FEE2E2', color: '#991B1B' }}
          >
            {reschedulingError}
          </Alert>
        )}

            {/* Job-Type Legend - Motion-style compact pills */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            mb: 1, // Motion-style: Minimal gap between legend and grid
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          {Object.entries(JOB_TYPE_COLORS)
            .filter(([key]) => key !== 'default')
            .map(([type, color]) => (
              <Chip
                key={type}
                label={JOB_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1)}
                sx={{
                  backgroundColor: color,
                  color: 'white',
                  fontSize: '0.75rem',
                  height: 28,
                  borderRadius: '9999px',
                  px: 1.5,
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              />
            ))}
        </Box>

        {/* FullCalendar Container - Motion-style grid */}
        {/* 
          HEIGHT CONTROL: Calendar fills remaining card space
          - Container uses flex: 1 to take all space after legend
          - Calendar height set to 100% to fill container completely
          - No inner scrollbars - only page scrolls
          - Grid shows full day (6am-11:59pm) without cutting off
        */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            margin: 0,
            padding: 0,
            height: '100%',
            '& .fc': {
              color: '#E5E7EB',
              fontFamily: 'inherit',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              margin: 0,
            },
            '& .fc-view-harness': {
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden',
              width: '100%',
              height: '100%',
            },
            '& .fc-scroller': {
              overflowY: 'auto !important', // Scroll through all time slots
              overflowX: 'hidden !important', // No horizontal scroll
              height: '100% !important',
              maxHeight: 'none !important',
              WebkitOverflowScrolling: 'touch',
            },
            '& .fc-scroller-liquid-absolute': {
              position: 'relative !important',
            },
            '& .fc-timeGrid-view .fc-timegrid-body': {
              overflowY: 'visible !important',
            },
            '& .fc-scrollgrid': {
              width: '100% !important',
              tableLayout: 'auto',
              margin: 0,
            },
            '& .fc-scrollgrid-sync-table': {
              width: '100% !important',
            },
            '& .fc-scrollgrid-section': {
              width: '100%',
            },
            '& .fc-header-toolbar': {
              display: 'none', // Hide default header (we have custom one)
            },
            '& .fc-daygrid-day, & .fc-timeGridDay-view .fc-daygrid-day': {
              backgroundColor: '#111827', // Motion-style: Clean background, no tints
            },
            '& .fc-col-header': {
              backgroundColor: '#111827',
              borderBottom: '1px solid rgba(255, 255, 255, 0.07)', // Motion-style: Light, thin lines
            },
            '& .fc-col-header-cell': {
              padding: '10px 6px', // Motion-style: Tighter padding for density
              borderRight: '1px solid rgba(255, 255, 255, 0.07)', // Thin grid lines
              minWidth: '130px', // Motion-style: Slightly narrower columns for density
              '&:last-child': {
                borderRight: 'none',
              },
            },
            '& .fc-col-header-cell-cushion': {
              color: '#E5E7EB',
              fontWeight: 600,
              fontSize: '0.875rem',
            },
            '& .fc-daygrid-day-number, & .fc-timeGridDay-view .fc-daygrid-day-number': {
              color: '#9CA3AF',
              padding: '8px',
              fontSize: '0.875rem',
            },
            // Motion-style today highlight: Subtle background tint
            '& .fc-day-today': {
              backgroundColor: 'rgba(59, 130, 246, 0.08)', // Motion-style: Very subtle tint (not heavy)
            },
            '& .fc-daygrid-day-top': {
              flexDirection: 'row',
            },
            '& .fc-daygrid-day.fc-day-today .fc-daygrid-day-number': {
              color: '#3B82F6',
              fontWeight: 700,
            },
            '& .fc-timegrid-slot': {
              borderTop: '1px solid rgba(255, 255, 255, 0.07)', // Motion-style: Light, thin grid lines
              height: '42px', // Motion-style: Compact slot height for density
              minHeight: '42px',
            },
            '& .fc-timegrid-slot-label': {
              borderRight: '1px solid rgba(255, 255, 255, 0.07)', // Thin grid lines
              padding: '4px 6px',
            },
            '& .fc-timegrid-slot-label-cushion': {
              color: '#9CA3AF',
              fontWeight: 600,
              fontSize: '0.75rem',
            },
            '& .fc-timegrid-col': {
              borderRight: '1px solid rgba(255, 255, 255, 0.07)', // Thin grid lines
            },
            // Motion-style today column highlight: Subtle background tint
            '& .fc-timegrid-col.fc-day-today': {
              backgroundColor: 'rgba(59, 130, 246, 0.08)', // Very subtle tint
              borderLeft: '2px solid rgba(59, 130, 246, 0.3)', // Subtle left border accent
              borderRight: '2px solid rgba(59, 130, 246, 0.3)', // Subtle right border accent
            },
            '& .fc-timegrid-axis': {
              borderRight: '1px solid rgba(255, 255, 255, 0.07)', // Thin grid lines
              backgroundColor: '#111827', // Same as grid background (no heavy tint)
              minWidth: '65px', // Motion-style: Slightly narrower time column
              width: '65px',
              flexShrink: 0,
            },
            '& .fc-timegrid-axis-cushion': {
              color: '#E5E7EB',
              fontWeight: 600,
              fontSize: '0.75rem',
            },
            '& .fc-timegrid-col': {
              minWidth: '130px', // Motion-style: Narrower columns for density
            },
            '& .fc-col-header': {
              width: '100%',
            },
            '& .fc-col-header-cell': {
              minWidth: '130px', // Match column width
            },
            '& .fc-scrollgrid': {
              border: 'none',
              borderRight: '1px solid rgba(255, 255, 255, 0.07)', // Thin grid lines
              borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
            },
            '& .fc-scrollgrid-section > table': {
              border: 'none',
            },
            // Motion-style events: Rounded rectangles with light background + colored border
            '& .fc-event': {
              borderRadius: '6px', // Motion-style: 4-6px radius
              padding: '6px 10px',
              borderWidth: '1px !important', // Colored border (set via borderColor)
              borderStyle: 'solid',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)', // Subtle shadow for lift
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': {
                transform: 'translateY(-1px)', // Subtle lift on hover
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
              },
            },
            '& .fc-event-title': {
              fontWeight: 600, // Bold title (first line)
              fontSize: '0.8125rem',
              lineHeight: 1.3,
              marginBottom: '2px',
            },
            '& .fc-event-time': {
              fontSize: '0.75rem',
              opacity: 0.85,
              fontWeight: 400, // Normal weight for time
            },
            '& .fc-timegrid-event': {
              borderRadius: '6px',
            },
            '& .fc-daygrid-event': {
              borderRadius: '6px',
            },
            '& .fc-button': {
              display: 'none', // Hide default buttons
            },
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={calendarView}
            headerToolbar={false}
            editable={true}
            droppable={true}
            selectable={false}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            events={calendarEvents}
            eventDrop={handleEventDrop}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            datesSet={handleViewChange}
            initialDate={selectedDate.toDate()}
            slotMinTime="06:00:00"
            slotMaxTime="23:59:00"
            allDaySlot={false}
            height="100%"
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            eventDisplay="block"
            // Motion-style event content: Custom two-line format (Title + Job #)
            // RENDERING: First line = "JobType – CustomerName" (bold), Second line = "Job #123" (normal)
            eventContent={(eventInfo) => {
              const { event } = eventInfo;
              const { typeLabel, jobId, customerName } = event.extendedProps || {};
              
              // Use extendedProps if available, otherwise parse from title
              const title = typeLabel && customerName 
                ? `${typeLabel} – ${customerName}`
                : event.title || 'Work Item';
              const jobNumber = jobId ? `Job #${jobId}` : '';
              
              return {
                html: `
                  <div style="font-weight: 600; font-size: 0.8125rem; line-height: 1.3; margin-bottom: 2px; color: #E5E7EB;">
                    ${title}
                  </div>
                  ${jobNumber ? `<div style="font-size: 0.75rem; opacity: 0.85; font-weight: 400; color: #E5E7EB;">${jobNumber}</div>` : ''}
                `
              };
            }}
            dayCellClassNames={(dateInfo) => {
              return moment(dateInfo.date).isSame(selectedDate, 'day') ? 'fc-day-selected' : '';
            }}
          />
        </Box>
      </Paper>

      {/* Quick View Modal */}
      <Dialog 
        open={quickViewOpen} 
        onClose={handleQuickViewClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1F2937',
            color: '#E5E7EB',
          }
        }}
      >
        <DialogTitle sx={{ color: '#E5E7EB' }}>Work Quick View</DialogTitle>
        <DialogContent>
          {quickViewWork && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#E5E7EB' }}>
                {quickViewWork.name}
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ color: '#9CA3AF' }}>
                {quickViewWork.description}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9CA3AF' }} gutterBottom>
                Scheduled: {quickViewWork.scheduledDate 
                  ? moment(quickViewWork.scheduledDate).format('MMMM D, YYYY h:mm A') 
                  : 'Not scheduled'}
              </Typography>
              {quickViewWork.team && (
                <Typography variant="body2" sx={{ color: '#3B82F6' }} gutterBottom>
                  Team: {quickViewWork.team}
                </Typography>
              )}
              {quickViewWork.parentJobName && (
                <Typography variant="body2" sx={{ color: '#9CA3AF' }} gutterBottom>
                  Parent Job: {quickViewWork.parentJobName}
                </Typography>
              )}
              {quickViewWork.attachments && quickViewWork.attachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#E5E7EB' }}>
                    Attachments:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {quickViewWork.attachments.map(att => {
                      const httpsUrl = ensureHttps(att.url);
                      logUrl(`Schedule Quick View Attachment ${att.id}`, httpsUrl);
                      return (
                        <Paper 
                          key={att.id} 
                          sx={{ 
                            p: 1, 
                            minWidth: 100, 
                            maxWidth: 120, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            backgroundColor: '#111827',
                          }}
                        >
                          {att.type === 'image' ? (
                            <img 
                              src={httpsUrl} 
                              alt={att.name} 
                              onLoad={() => logUrl(`Schedule Attachment Image Load ${att.id}`, httpsUrl)}
                              onError={(e) => {
                                console.error('Error loading attachment image:', httpsUrl);
                                logUrl(`Schedule Attachment Image Error ${att.id}`, httpsUrl);
                              }}
                              style={{ width: '100%', maxHeight: 60, objectFit: 'cover', marginBottom: 4 }} 
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              {att.name}
                            </Typography>
                          )}
                          <Button 
                            size="small" 
                            href={httpsUrl} 
                            target="_blank" 
                            sx={{ mt: 1, color: '#3B82F6' }}
                          >
                            View
                          </Button>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#111827' }}>
          <Button 
            onClick={handleQuickViewClose}
            sx={{ color: '#E5E7EB' }}
          >
            Close
          </Button>
          {quickViewWork && quickViewWork.parentJobId && (
            <Button 
              onClick={() => { 
                handleQuickViewClose(); 
                navigate(`/jobs/${quickViewWork.parentJobId}`); 
              }} 
              variant="contained"
              sx={{ backgroundColor: '#3B82F6' }}
            >
              View Job Details
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Schedule Estimate Dialog */}
      <Dialog 
        open={scheduleEstimateOpen} 
        onClose={() => setScheduleEstimateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1F2937',
            color: '#E5E7EB',
          }
        }}
      >
        <DialogTitle sx={{ color: '#E5E7EB' }}>Schedule Estimate</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DateTimePicker
              label="Date & Time"
              value={estimateSchedule.date}
              onChange={(newDate) => setEstimateSchedule({ ...estimateSchedule, date: newDate })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      color: '#E5E7EB',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                    },
                    '& label': {
                      color: '#9CA3AF',
                    },
                  }
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#9CA3AF' }}>Team</InputLabel>
              <Select
                value={estimateSchedule.team}
                onChange={(e) => setEstimateSchedule({ ...estimateSchedule, team: e.target.value })}
                label="Team"
                sx={{
                  color: '#E5E7EB',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#9CA3AF',
                  },
                }}
              >
                {teams?.map((team) => (
                  <MenuItem key={team.id} value={team.id} sx={{ color: '#E5E7EB' }}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              multiline
              rows={4}
              value={estimateSchedule.notes}
              onChange={(e) => setEstimateSchedule({ ...estimateSchedule, notes: e.target.value })}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#E5E7EB',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                },
                '& label': {
                  color: '#9CA3AF',
                },
              }}
            />
            {conflict && (
              <Box sx={{ color: '#EF4444', mt: 1, p: 1.5, backgroundColor: '#FEE2E2', borderRadius: 1 }}>
                Warning: This team is already scheduled for another work item at this time.<br />
                <b>{conflict.name || conflict.description || 'Work Item'}</b> for {conflict.customerName} at {moment(conflict.scheduledDate).format('MMMM D, YYYY h:mm A')}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#111827' }}>
          <Button 
            onClick={() => setScheduleEstimateOpen(false)}
            sx={{ color: '#E5E7EB' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleScheduleEstimate} 
            variant="contained"
            disabled={!estimateSchedule.team || !!conflict}
            sx={{ backgroundColor: '#3B82F6' }}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
